/* ============================================================
   assets/xp-shopify.js
   Commerce adapter for Toothache OS.

   The kernel (xp-desktop.js) knows nothing about Shopify. It exposes
   window.ToothacheOS.hooks; this file fills them in so windows read from
   the live store instead of the mock catalog.

     hooks.collection(handle) -> Section Rendering API
     hooks.product(handle)    -> /products/{handle}.js
     hooks.add(handle, size)  -> /cart/add.js
     hooks.cart(container)    -> /cart.js  + /cart/change.js

   Load after xp-desktop.js.
   ============================================================ */
(() => {
  "use strict";
  const OS = window.ToothacheOS;
  if (!OS) return console.warn("[toothache] kernel missing; desktop stayed on mock data");

  const { WM, ICON, esc, $, toast } = OS;
  const R = (OS.DATA.routes) || { cart: "/cart", cart_add: "/cart/add", cart_change: "/cart/change", checkout: "/checkout" };
  const cache = new Map();               // handle -> section HTML
  const fmt = (cents) => "$" + (cents / 100).toFixed(2);
  /* product.js and cart.js return the ORIGINAL upload URL — often 3–4000px.
     Shopify's CDN resizes on demand from a width param; a 720px hero and a
     120px cart thumb are an order of magnitude lighter. */
  const sized = (u, w) => (u ? u + (String(u).includes("?") ? "&" : "?") + "width=" + w : u);
  /* Shopify names the lone variant of an option-less product "Default Title".
     It's an internal placeholder, never something to show a shopper — strip it
     everywhere a variant name is displayed. */
  const realVariant = (t) => (!t || t === "Default Title" ? "" : t);

  const j = (url, body) =>
    fetch(url, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    }).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });

  const spinner = (label) =>
    `<div class="empty"><p>${esc(label)}</p></div>`;

  /* ---------- collection windows ---------- */
  async function loadSection(handle, container, url) {
    const key = url || handle;
    try {
      if (!cache.has(key)) {
        const path = url || `/collections/${handle}`;
        const sep = path.includes("?") ? "&" : "?";
        const res = await fetch(`${path}${sep}section_id=xp-collection-grid`);
        if (!res.ok) throw new Error(res.status);
        cache.set(key, await res.text());
      }
      container.innerHTML = cache.get(key);
      wireGrid(container, handle);
    } catch (e) {
      container.innerHTML = `<div class="empty"><p>This collection didn't load.<br>
        <button class="btn" type="button" data-retry>Try again</button>
        or <a href="/collections/${esc(handle)}">open it as a normal page</a>.</p></div>`;
      const b = $("[data-retry]", container);
      if (b) b.addEventListener("click", () => { cache.delete(key); loadSection(handle, container, url); });
    }
  }

  function wireGrid(container, handle) {
    container.querySelectorAll("[data-handle]").forEach((a) =>
      a.addEventListener("click", (e) => {
        if (e.metaKey || e.ctrlKey || e.button === 1) return;   // let real links work
        e.preventDefault();
        openProduct(a.dataset.handle);
      })
    );
    container.querySelectorAll("[data-go]").forEach((a) =>
      a.addEventListener("click", (e) => {
        if (e.metaKey || e.ctrlKey || e.button === 1) return;   // let real links work
        e.preventDefault();
        a.dataset.go === "__all" ? OS.openCollections() : openCollection(a.dataset.go);
      })
    );
    const more = $("[data-more-url]", container);
    if (more) {
      more.addEventListener("click", async () => {
        const label = more.textContent;
        more.disabled = true; more.textContent = "Loading\u2026";
        try {
          const res = await fetch(`${more.dataset.moreUrl}&section_id=xp-collection-grid`);
          if (!res.ok) throw new Error(res.status);
          const html = await res.text();
          const grid = document.createElement("div");
          grid.innerHTML = html;
          const incoming = grid.querySelector("[data-grid]");
          more.remove();
          if (incoming) {
            $("[data-grid]", container).append(...incoming.children);
            wireGrid(container, handle);
          }
        } catch (e) {
          /* leave the button usable instead of stuck on "Loading…" */
          more.disabled = false; more.textContent = label;
          toast("Couldn't load more", "Check your connection and try again.");
        }
      });
    }
  }

  function openCollection(handle) {
    const title = handle === "all" ? "Shop All" : OS.collTitle(handle);
    const path = handle === "all" ? "/collections/all" : `/collections/${handle}`;
    WM.open("coll:" + handle, {
      title: `${title} \u2014 ${OS.DATA.shop.name}`,
      icon: handle === "all" ? ICON.computer : ICON.folder,
      w: 900, h: 610, flush: true,
      menus: ["File", "Edit", "View", "Favorites"],
      status: [title, "Toothache Shop"],
      render(c) {
        c.innerHTML = spinner("Opening " + title + "\u2026");
        loadSection(handle === "all" ? "all" : handle, c, path);
      },
    });
  }

  /* ---------- product windows ---------- */
  async function openProduct(handle) {
    WM.open("prod:" + handle, {
      title: "Loading\u2026", icon: ICON.docs, w: 720, h: 550,
      menus: ["File", "Share"],
      async render(c, win) {
        c.innerHTML = spinner("Reading product\u2026");
        let p;
        try {
          p = await j(`/products/${handle}.js`);
        } catch (e) {
          c.innerHTML = `<div class="empty"><p>Couldn't load this product.<br>
            <a href="/products/${esc(handle)}" target="_blank" rel="noopener">Open it in a new tab</a>.</p></div>`;
          return;
        }
        $(".titlebar h2", win).textContent = `${p.title} \u2014 Properties`;

        let variant = p.variants.find((v) => v.available) || p.variants[0];
        /* /products/{handle}.js returns options as OBJECTS ({name, values}),
           so the old `p.options[0]` stringified to "[object Object]". And a
           product with no real options still carries one variant titled
           "Default Title" — Shopify's placeholder — which is noise to a
           shopper, so the whole picker is skipped for it. */
        const optionName = p.options && p.options.length
          ? p.options.map((o) => (o && o.name) || o).filter(Boolean).join(" / ")
          : "Option";
        const hasOptions = !(p.variants.length === 1 && p.variants[0].title === "Default Title");

        /* Every shot on the listing, not just the featured one. Shopify orders
           p.images the way the merchant arranged them in admin, so keep that
           order and just make sure the featured image leads. */
        const shots = (p.images && p.images.length ? p.images.slice()
                      : (p.featured_image ? [p.featured_image] : []));
        if (p.featured_image) {
          const at = shots.indexOf(p.featured_image);
          if (at > 0) shots.splice(0, 0, shots.splice(at, 1)[0]);
        }
        let shot = 0;   /* survives variant repaints; a variant with its own photo jumps to it */

        const paint = () => {
          const img = shots[shot];
          c.innerHTML = `
            <div class="pdp">
              <div class="gallery">
                <div class="hero" style="background:linear-gradient(160deg,#ffd0e444,#ffd0e4)">
                  ${img ? `<button type="button" class="hero-zoom" data-zoom
                      aria-label="View larger image ${shot + 1} of ${shots.length}">
                      <img src="${sized(img, 720)}" alt="${esc(p.title)}" decoding="async">
                      <span class="zoom-cue" aria-hidden="true">${ICON.search}</span>
                    </button>` : ""}
                </div>
                ${shots.length > 1 ? `<div class="thumbs" role="group" aria-label="Product images">
                  ${shots.map((u, i) => `<button type="button" class="thumb-btn" data-shot="${i}"
                      aria-pressed="${i === shot}" aria-label="Image ${i + 1} of ${shots.length}">
                      <img src="${sized(u, 160)}" alt="" loading="lazy" decoding="async"></button>`).join("")}
                </div>` : ""}
              </div>
              <div class="info">
                <h3>${esc(p.title)}</h3>
                <div class="price">${fmt(variant.price)}</div>
                <div>${p.description || ""}</div>
                ${hasOptions ? `<fieldset><legend>${esc(optionName)}</legend>
                  <div class="sizes">${p.variants.map((v) => `
                    <button class="size" type="button" data-v="${v.id}"
                      ${v.available ? "" : "disabled"}
                      aria-pressed="${v.id === variant.id}">${esc(v.title)}</button>`).join("")}
                  </div>
                </fieldset>` : ""}
                <div class="row">
                  <button class="btn primary" type="button" data-add ${variant.available ? "" : "disabled"}>
                    ${variant.available ? "Add to bag" : "Sold out"}</button>
                  <a class="btn" href="/products/${esc(handle)}" target="_blank" rel="noopener">Open in new tab</a>
                </div>
              </div>
            </div>`;
          c.querySelectorAll(".size").forEach((b) =>
            b.addEventListener("click", () => {
              variant = p.variants.find((v) => String(v.id) === b.dataset.v);
              /* if this variant has its own photo, show it */
              const vi = variant.featured_image && variant.featured_image.src;
              if (vi) { const at = shots.indexOf(vi); if (at > -1) shot = at; }
              paint();
            })
          );
          c.querySelectorAll("[data-shot]").forEach((b) =>
            b.addEventListener("click", () => { shot = +b.dataset.shot; paint(); })
          );
          const z = $("[data-zoom]", c);
          if (z) z.addEventListener("click", () => openViewer(p.title, shots, shot));
          $("[data-add]", c).addEventListener("click", () => addVariant(variant.id, p.title, variant.title));
        };
        paint();
      },
    });
  }

  /* ---------- image viewer ----------
     The old theme had a lightbox; this is the same job as a desktop window —
     a Picture Viewer you can drag, maximize and keep open beside the product.
     Click (or the magnifier) toggles between fit-to-window and 100% zoom, and
     at 100% the image pans by dragging. Arrows and the keyboard step through
     the set. */
  function openViewer(title, shots, start) {
    let i = Math.max(0, start | 0), zoomed = false;
    WM.open("viewer:" + title, {
      title: `${title} \u2014 Picture Viewer`, icon: ICON.display,
      w: 760, h: 640, flush: true, menus: ["File", "View"],
      status: [`Image ${i + 1} of ${shots.length}`],
      render(c, win) {
        c.classList.add("viewer");
        c.innerHTML = `
          <div class="vw-stage" data-stage>
            <img data-img alt="${esc(title)}" decoding="async">
          </div>
          <div class="vw-bar">
            <button class="btn" type="button" data-prev aria-label="Previous image">\u2039</button>
            <span class="vw-count" data-count aria-live="polite"></span>
            <button class="btn" type="button" data-next aria-label="Next image">\u203a</button>
            <button class="btn" type="button" data-zoom aria-pressed="false" style="margin-left:auto">Zoom 100%</button>
          </div>`;
        const stage = $("[data-stage]", c), im = $("[data-img]", c), count = $("[data-count]", c);
        const zb = $("[data-zoom]", c);

        const show = () => {
          /* 1600px is plenty for 100% on a desktop window and a fraction of
             the original upload's weight */
          im.src = sized(shots[i], zoomed ? 1600 : 900);
          stage.dataset.zoom = String(zoomed);
          zb.setAttribute("aria-pressed", String(zoomed));
          zb.textContent = zoomed ? "Fit to window" : "Zoom 100%";
          count.textContent = `${i + 1} / ${shots.length}`;
          const s = win.querySelectorAll(".statusbar span");
          if (s[0]) s[0].textContent = `Image ${i + 1} of ${shots.length}`;
          const single = shots.length < 2;
          $("[data-prev]", c).disabled = single;
          $("[data-next]", c).disabled = single;
        };
        const step = (d) => { i = (i + d + shots.length) % shots.length; zoomed = false; stage.scrollTo(0, 0); show(); };

        $("[data-prev]", c).addEventListener("click", () => step(-1));
        $("[data-next]", c).addEventListener("click", () => step(1));
        const toggle = () => { zoomed = !zoomed; show(); };
        zb.addEventListener("click", toggle);
        im.addEventListener("click", toggle);

        /* drag to pan while zoomed */
        let panning = false, sx = 0, sy = 0, sl = 0, st = 0;
        stage.addEventListener("pointerdown", (e) => {
          if (!zoomed) return;
          panning = true; sx = e.clientX; sy = e.clientY; sl = stage.scrollLeft; st = stage.scrollTop;
          stage.setPointerCapture(e.pointerId);
        });
        stage.addEventListener("pointermove", (e) => {
          if (!panning) return;
          stage.scrollLeft = sl - (e.clientX - sx);
          stage.scrollTop = st - (e.clientY - sy);
        });
        const stop = (e) => {
          if (!panning) return;
          panning = false;
          try { stage.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
        };
        stage.addEventListener("pointerup", stop);
        stage.addEventListener("pointercancel", stop);

        win.addEventListener("keydown", (e) => {
          if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
          else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
          else if (e.key === "Enter" && document.activeElement === im) toggle();
        });
        im.tabIndex = 0;
        show();
      },
    });
  }

  /* ---------- cart ---------- */
  async function addVariant(id, title, size) {
    const btn = document.activeElement;
    if (btn && btn.dataset && "add" in btn.dataset) { btn.disabled = true; btn.textContent = "Adding\u2026"; }
    try {
      await j(R.cart_add + ".js", { items: [{ id, quantity: 1 }] });
      await syncCount();
      /* Glitter pops from the bag in the tray, so the eye is pulled to where
         the item actually went rather than to the button it left. */
      if (window.XPCelebrate) window.XPCelebrate.fromEl(document.getElementById("tray-cart") || btn, { count: 22 });
      const v = realVariant(size);
      toast("Added to bag", `${esc(title)}${v ? " \u2014 " + esc(v) : ""}. Open the bag when you're ready.`);
      const cw = WM.wins.get("cart");
      if (cw) renderCart($(".win-content", cw.el));
    } catch (e) {
      toast("Couldn't add that", "The size may have just sold out. Refresh and try again.");
    } finally {
      if (btn && btn.dataset && "add" in btn.dataset) { btn.disabled = false; btn.textContent = "Add to bag"; }
    }
  }

  async function syncCount() {
    const cart = await j(R.cart + ".js");
    /* the kernel owns the tray bag artwork; never poke at #cart-count directly */
    OS.setCartCount(cart.item_count,
      cart.item_count === 0 ? "Empty"
        : `${cart.item_count} item${cart.item_count === 1 ? "" : "s"} \u00b7 ${fmt(cart.total_price)}`);
    return cart;
  }

  async function renderCart(c) {
    c.innerHTML = spinner("Opening cart\u2026");
    let cart;
    try { cart = await j(R.cart + ".js"); }
    catch (e) { c.innerHTML = `<div class="empty"><p>Your bag didn't load. <a href="${R.cart}">Open the bag page</a>.</p></div>`; return; }

    if (!cart.items.length) {
      c.innerHTML = `<div class="empty">${ICON.bag}
        <p>Nothing in the bag yet.<br>Open Shop All to start filling it.</p>
        <button class="btn primary" type="button" data-shop>Open Shop All</button></div>`;
      $("[data-shop]", c).addEventListener("click", () => openCollection("all"));
      return;
    }

    c.innerHTML = cart.items.map((l, i) => `
      <div class="cart-line">
        <div class="sw" style="background:#ffd0e4">
          ${l.image ? `<img src="${sized(l.image, 120)}" alt="" loading="lazy" decoding="async" style="width:100%;height:100%;object-fit:cover;border-radius:4px">` : ""}
        </div>
        <div><b>${esc(l.product_title)}</b><small>${realVariant(l.variant_title) ? esc(realVariant(l.variant_title)) + " \u00b7 " : ""}${fmt(l.price)}</small></div>
        <div class="qty">
          <button class="qbtn" type="button" data-line="${i + 1}" data-q="${l.quantity - 1}" aria-label="Remove one">\u2212</button>
          <span>${l.quantity}</span>
          <button class="qbtn" type="button" data-line="${i + 1}" data-q="${l.quantity + 1}" aria-label="Add one">+</button>
        </div>
      </div>`).join("") + `
      <div class="total"><span>Subtotal</span><span>${fmt(cart.total_price)}</span></div>
      <p style="color:#8a72a0">Shipping and taxes are calculated at checkout.</p>
      <button class="btn primary" type="button" style="width:100%" data-checkout>Check out</button>`;

    c.querySelectorAll("[data-line]").forEach((b) =>
      b.addEventListener("click", async () => {
        b.disabled = true;
        await j(R.cart_change + ".js", { line: +b.dataset.line, quantity: +b.dataset.q });
        await syncCount();
        renderCart(c);
      })
    );
    $("[data-checkout]", c).addEventListener("click", () => goToCheckout(cart));
  }

  /* ---------- checkout handoff ----------
     Shopify's checkout is hosted by Shopify and cannot be themed from theme
     files (it is branded in Admin -> Settings -> Checkout instead). So the
     job here is to make the SEAM feel deliberate rather than like falling
     out of the site: same tab, a short branded "connecting" curtain, and a
     note in sessionStorage so we can celebrate when they come back.

     Same tab is deliberate. A new tab for checkout leaves an abandoned cart
     page behind, breaks the back button, and is where most of the "it opened
     somewhere else" feeling came from. */
  function goToCheckout(cart) {
    try {
      sessionStorage.setItem("toothache:pending", JSON.stringify({
        at: Date.now(),
        count: cart ? cart.item_count : 0,
        total: cart ? cart.total_price : 0,
      }));
    } catch { /* private mode — we just skip the celebration later */ }

    const curtain = document.createElement("div");
    curtain.className = "xp-curtain";
    curtain.setAttribute("role", "status");
    curtain.innerHTML = `
      <div class="xp-curtain-win">
        <div class="xp-curtain-bar"><b>Connecting to secure checkout</b></div>
        <div class="xp-curtain-body">
          <p>Taking you to Shopify's secure checkout to finish your order.</p>
          <div class="xp-prog"><i></i></div>
          <small>Your bag travels with you. Payment is handled by Shopify \u2014 we never see your card.</small>
        </div>
      </div>`;
    document.body.appendChild(curtain);
    /* Navigate on the next frame so the curtain actually paints first. */
    requestAnimationFrame(() => setTimeout(() => (window.location.href = R.checkout), 420));
  }

  /* ---------- welcome back after an order ----------
     Shopify gives the storefront no direct "they just ordered" signal on
     non-Plus plans, so this is a deliberate heuristic: we noted that they
     left for checkout with N items; if they return within the hour and the
     cart is now empty, the order went through. Worst case is a missed
     celebration, never a false one on a still-full cart. */
  async function welcomeBack() {
    let pending;
    try { pending = JSON.parse(sessionStorage.getItem("toothache:pending") || "null"); } catch { return; }
    if (!pending) return;
    if (Date.now() - pending.at > 3600e3) { sessionStorage.removeItem("toothache:pending"); return; }

    let cart;
    try { cart = await (await fetch(R.cart + ".js", { headers: { Accept: "application/json" } })).json(); }
    catch { return; }
    if (cart.item_count !== 0) return;   /* still shopping — say nothing */

    sessionStorage.removeItem("toothache:pending");
    if (window.XPCelebrate) window.XPCelebrate.shower();

    WM.open("order-done", {
      title: "Order Complete", icon: OS.ICON.bag, w: 460, h: 300, modal: true,
      render(c) {
        c.innerHTML = `
          <h3 style="margin-top:0">Thank you! \u2728</h3>
          <p>Your order is in. A confirmation email is on its way with your
             receipt and tracking as soon as it ships.</p>
          <p style="color:#8a72a0">Order details also live in your account, and in the
             confirmation email if you checked out as a guest.</p>
          <div class="row">
            <button class="btn primary" type="button" data-ok>Back to the desktop</button>
            <a class="btn" href="/account" target="_blank" rel="noopener">My orders</a>
          </div>`;
        $("[data-ok]", c).addEventListener("click", () => WM.close("order-done"));
      },
    });
  }
  /* Wait a beat so the desktop has finished booting its windows. */
  setTimeout(welcomeBack, 900);

  /* ---------- wallpaper persistence ----------
     Storefront JS cannot write customer metafields directly. Mirror the choice
     to an App Proxy route that writes it with the Admin API; if the proxy isn't
     built yet this fails quietly and localStorage still holds the preference. */
  OS.hooks.saveWallpaper = (id) => {
    if (!OS.DATA.shop.customer) return;
    fetch("/apps/toothache/prefs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ wallpaper: id }),
    }).catch(() => {});
  };


  /* ---------- newsletter goes through Shopify's own customer form ----------
     No third-party list, no API key: we fill the offscreen {% form 'customer' %}
     and submit it, so the subscriber appears in Shopify with the marketing
     consent flag set. */
  OS.hooks.subscribe = (email) => {
    const form = document.getElementById("xp-newsletter-form");
    const field = document.getElementById("xp-newsletter-email");
    if (!form || !field) return;
    field.value = email;
    form.submit();
  };

  /* ---------- consent drives Shopify's Customer Privacy API ----------
     Marketing and analytics pixels stay off until a visitor opts in, and the
     choice survives a reload because the kernel keeps it in localStorage. */
  function applyConsent(value) {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) return;                       /* not enabled on this shop */
    const grant = value === "all";
    try {
      api.setTrackingConsent(
        { analytics: grant, marketing: grant, preferences: true, sale_of_data: grant },
        () => {}
      );
    } catch (e) { /* API surface differs across shops; never break the desktop */ }
  }
  document.addEventListener("toothache:consent", (e) => applyConsent(e.detail.value));
  const prior = localStorage.getItem("toothache:consent");
  if (prior) applyConsent(prior);

  /* ---------- install ---------- */
  OS.hooks.collection = openCollection;
  OS.hooks.product = openProduct;
  OS.hooks.cart = renderCart;
  OS.hooks.add = (handle) => openProduct(handle);   // quick-add needs a variant, so open the window

  if (OS.DATA.shop && OS.DATA.shop.customer) OS.state.signedIn = true;
  if (OS.DATA.wallpaper) {
    OS.state.wallpaper = localStorage.getItem("toothache:wallpaper") || OS.DATA.wallpaper;
    OS.paintWallpaper();
  }
})();